import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BsFillTrashFill, BsPencil, BsBookmarksFill } from "react-icons/bs";
import { useForm } from 'react-hook-form';
import { toast, ToastContainer } from "react-toastify";

import SubscriptionTicketService from '../../../../services/SubscriptionTicketService';
import PlanService from '../../../../services/PlanService';
import UsersService from '../../../../services/UsersService';
import ProductTicketService from '../../../../services/ProductTicketService';

import EditTicketModal from '../modals/EditTicketModal';
import ConfirmModal from '../../../../modals/ConfirmModal';
import CreateSubscriptionTicketModal from '../modals/CreateSubscriptionTicketModal';
import EditSubscriptionTicketDetailsModal from '../modals/EditSubscriptionTicketDetailsModal';
import Assign from '../../../../modals/Assign';
import { statusClasses, priorityClasses, priorityOptions, statusOptions } from '../../../../utils/OptionUtils';
import ListProductTicket from './ListProductTicket';
import CreateProductTicket from '../modals/CreateProductTicket';
import CustomersService from '../../../../services/CustomersService';

export const ListSubscriptionTicket = ({ tickets: initialTickets, customerId, company, toggleView, planId, setCustomer, subscriptionId }) => {
    const { formState: { errors }, control, handleSubmit, reset, register } = useForm();
    const [showEditTicketModal, setShowEditTicketModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [ticketId, setTicketId] = useState(null);
    const [columnType, setColumnType] = useState(null);
    const [tickets, setTickets] = useState(initialTickets || []);
    const [productTickets, setProductTickets] = useState([]);
    const [plan, setPlan] = useState(null);
    const [usersList, setUsersList] = useState([]);
    const [activeTab, setActiveTab] = useState('basic')
    const [showCreateProductTicket, setShowCreateProductTicket] = useState(false);
    const [productOptions, setProductOptions] = useState([]);
    const [ticket, setTicket] = useState({});
    const [validProductAvailable, setValidProductAvailable] = useState(false);

    const fetchCustomerPlans = useCallback(async () => {
        try {
            if (!planId || typeof planId === 'undefined') return;
            const response = await PlanService.get(planId);
            if (response.status === 200) {
                setPlan(response?.data?.data);
            } else {
                toast.error(response?.data?.message);
            }
        } catch (error) {
            console.error('Error in fetch customer plans', error);
            toast.error(error?.message || "An error occurred");
        }
    }, [planId]);

    useEffect(() => {
        fetchCustomerPlans();
    }, [fetchCustomerPlans]);

    const handleEdit = async (ticket) => {
        setTicket(ticket);
        setShowEditModal(true);
    };

    const handleDelete = (id) => {
        setTicketId(id);
        setShowConfirmModal(true);
    };

    const deleteTicket = async () => {
        if (!ticketId) return;
        try {
            const response = await SubscriptionTicketService.delete(ticketId);
            if (response.status === 200) {
                toast.success(response?.data?.message);
                setTickets(tickets.filter(ticket => ticket.id !== ticketId));
                setShowConfirmModal(false);
                toggleView('ticket', 'card');
            } else {
                toast.error(response?.data?.message);
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    };

    const handleColumnChange = (record, type) => {
        setTicketId(record.id);
        setColumnType(type);
        setShowEditTicketModal(true);
        reset({ [type]: record[type] });
    };

    const handleClose = () => {
        setShowEditTicketModal(false);
        setShowConfirmModal(false);
        setShowCreateTicketModal(false);
        setShowEditModal(false);
        setShowAssignModal(false);
        setShowCreateProductTicket(false);
        toggleView('ticket', 'card');
        reset();
    };

    const handleCreate = async () => {
        try {
            if (activeTab === 'premium') {
                setShowCreateTicketModal(true);
                return;
            }
            if (activeTab === 'basic') {
                const response = await CustomersService.customerProductList(customerId);
                if (response.status === 200) {
                    setProductOptions(response?.data?.data);
                    setShowCreateProductTicket(true);
                    return;
                } else {
                    toast.error(response?.data?.message);
                }
                return;
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    };

    const onSubmit = async (data) => {
        try {
            const service = activeTab === 'basic' ? ProductTicketService : SubscriptionTicketService
            const response = await service.changeStatusOrPriority(ticketId, { data }, columnType);
            if (response.status === 200) {
                toast.success(response?.data?.message);
                if (activeTab === 'premium') {
                    setTickets(tickets.map(ticket => ticket.id === ticketId ? { ...ticket, [columnType]: data[columnType] } : ticket));
                } else {
                    setProductTickets(productTickets.map(ticket => ticket.id === ticketId ? { ...ticket, [columnType]: data[columnType] } : ticket));
                }
                setShowEditTicketModal(false);
                reset();
            } else {
                toast.error(response?.data?.message);
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    };

    const onTicketSubmit = async (data) => {
        data.subscription_id = activeTab === 'premium' ? subscriptionId : null;
        if (activeTab === 'basic') {
            data.customer_id = customerId
        }

        try {
            const service = activeTab === 'basic' ? ProductTicketService : SubscriptionTicketService;
            const response = await service.create({ data });

            if (response.status === 201) {
                toast.success(response?.data?.message);
                setProductTickets(prevTickets => [...prevTickets, response?.data?.data]);
                if (activeTab === 'premium') {
                    setTickets(prevTickets => [...prevTickets, response?.data?.data]);
                    setCustomer(prevCustomer => ({
                        ...prevCustomer,
                        subscriptions: prevCustomer.subscriptions?.map(sub => {
                            if (sub.id === subscriptionId) {
                                return {
                                    ...sub,
                                    subscriptionTickets: [
                                        ...sub.subscriptionTickets,
                                        response?.data?.data
                                    ]
                                };
                            }
                            return sub;
                        })
                    }));
                }

                handleClose();
                toggleView('ticket', 'card');
                reset();
            } else {
                toast.error(response?.data?.message);
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    };

    const handleUpdate = async (data) => {
        data.subscription_id = subscriptionId || '';
        try {
            const response = await SubscriptionTicketService.update(ticket.id, { data });
            if (response.status === 200) {
                toast.success(response?.data?.message);
                setTickets(tickets.map(ticket => ticket.id === ticketId ? { ...ticket, ...data } : ticket));
                setShowEditModal(false);
                toggleView('ticket', 'card');
                reset();
            } else {
                toast.error(response?.data?.message);
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await UsersService.list();
            if (response.status === 200) {
                const data = response?.data?.data;
                const formattedUsers = data.map(user => ({
                    value: user.id,
                    label: user.name + ' (' + user.email + ')',
                }));
                setUsersList(formattedUsers);
                return formattedUsers;
            } else {
                toast.error(response?.data?.message);
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    }

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleAssign = (id) => {
        setTicketId(id);
        setShowAssignModal(true);
    }
    const onSubmitAssign = async (data) => {
        const assigned_to = data.assignee[0]?.value;
        data.assigned_to = assigned_to;
        delete data.assignee;
        const id = data?.ticket_id || ticketId;
        try {
            const service = activeTab === 'basic' ? ProductTicketService : SubscriptionTicketService;
            const response = await service.assign(id, { data });
            if (response.status === 200) {
                toast.success(response?.data?.message);
                const { assigned_to: assignedToId, assignedTo } = response?.data?.data;
                if (activeTab === 'premium') {
                    setTickets(prevTickets =>
                        prevTickets.map(ticket =>
                            ticket.id === id
                                ? { ...ticket, assigned_to: assignedToId, assignedTo }
                                : ticket
                        )
                    );
                } else {
                    setProductTickets(prevTickets =>
                        prevTickets.map(ticket =>
                            ticket.id === id
                                ? { ...ticket, assigned_to: assignedToId, assignedTo }
                                : ticket
                        )
                    );
                }
                setShowAssignModal(false);
                reset();
            } else {
                toast.error(response?.data?.message);
            }
        } catch (error) {
            console.error('error', error);
            toast.error(error?.message || "An error occurred");
        }
    };

    useEffect(() => {
        setShowCreateProductTicket(false);
        setShowCreateTicketModal(false);
    }, [activeTab]);

    const fetchValidProductList = useCallback(async () => {
        try {
            const response = await CustomersService.validProductList(customerId);
            if (response.status === 200) {
                console.log("✅ Setting valid product available: TRUE");
                setValidProductAvailable(true);
            } else {
                console.log("❌ Setting valid product available: FALSE");
                setValidProductAvailable(false);
            }
        } catch (error) {
            if (error?.response?.status === 404) {
                console.log("❌ Setting valid product available: FALSE");
                setValidProductAvailable(false);
            }
            toast.error(error?.message || 'An error occurred.');
        }
    }, [customerId, setValidProductAvailable]);
    useEffect(() => {
        if (customerId) {
            fetchValidProductList();
        }
    }, [customerId, fetchValidProductList]);

    return (
        <>
            <div className="container-fluid">
                <div className="card row">
                    <div className="card-header">
                        <div className="card-title">
                            <div className="row align-items-end">
                                <div className="col">
                                    <h2 className="header-title">{company}</h2>
                                </div>
                                <div className="col-auto">
                                    {activeTab === "premium" ? (
                                        planId ? (
                                            tickets?.length < plan?.no_of_tickets ? (
                                                <button className="btn btn-primary btn-sm" onClick={handleCreate}>
                                                    Create Ticket
                                                </button>
                                            ) : (
                                                <button className="text-white btn btn-warning btn-sm">
                                                    You have reached the ticket limit for your plan. Please upgrade your plan.
                                                </button>
                                            )
                                        ) : (
                                            <button className="text-white btn btn-danger btn-sm">
                                                Please purchase a subscription to create tickets.
                                            </button>
                                        )
                                    ) : (
                                        validProductAvailable ? (
                                            <button className="btn btn-primary btn-sm" onClick={handleCreate}>
                                                Create Ticket
                                            </button>
                                        ) : (
                                            <button className="text-white btn btn-danger btn-sm">
                                                Please purchase a product to create tickets.
                                            </button>
                                        )
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card-body">
                        <ul className='nav nav-tabs nav-tabs-sm nav-justified'>
                            <li className="nav-item">
                                <button className={`nav-link ${activeTab === 'basic' ? 'active text-primary' : ''}`}
                                    style={{ padding: '0.3rem 0.6rem' }}
                                    onClick={() => setActiveTab('basic')}
                                >Basic Support</button>
                            </li>
                            <li className="nav-item">
                                <button className={`nav-link ${activeTab === 'premium' ? 'active text-primary' : ''}`}
                                    style={{ padding: '0.3rem 0.6rem' }}
                                    onClick={() => setActiveTab('premium')}
                                >Premium Support</button>
                            </li>
                        </ul>
                        <div className="list-group list-group-flush list-group-activity my-n3">
                            <div className="list-group-item">
                                <div className="row">
                                    <div className="table-responsive">
                                        {activeTab === 'premium' ? (
                                            <table className="table table-sm table-hover table-nowrap card-table">
                                                <thead>
                                                    <tr>
                                                        <th scope='row'>#</th>
                                                        <th>Ticket No</th>
                                                        <th>Plan Name</th>
                                                        <th>Title</th>
                                                        <th>Checklist</th>
                                                        <th>Assign To</th>
                                                        <th>Priority</th>
                                                        <th>Status</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tickets.length > 0 ? (
                                                        tickets.map((row, index) => (
                                                            <tr key={index}>
                                                                <td> {index + 1} </td>
                                                                <td><Link to={`/tickets/${row?.id}/premium`}>{row?.ticket_no || 'N/A'}</Link></td>
                                                                <td>{row?.subscription?.plan?.name || 'N/A'}</td>
                                                                <td>{row?.title || 'No Title'}</td>
                                                                <td>{row?.checklist?.name || 'N/A'}</td>
                                                                <td>{row?.assignedTo?.name || 'Unassigned'}</td>
                                                                <td>
                                                                    <span
                                                                        className={`editable-field badge ${priorityClasses[row?.priority]}`}
                                                                        onClick={() => handleColumnChange(row, 'priority')}
                                                                    >
                                                                        {row?.priority?.charAt(0).toUpperCase() + row?.priority.slice(1) || 'N/A'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span
                                                                        className={`editable-field badge ${statusClasses[row?.status]}`}
                                                                        onClick={() => handleColumnChange(row, 'status')}
                                                                    >
                                                                        {row?.status?.charAt(0).toUpperCase() + row?.status.slice(1) || 'N/A'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <Link to="#" onClick={() => handleEdit(row)}><BsPencil title='Edit' /></Link> |
                                                                    <Link to="#" onClick={() => handleDelete(row?.id)}><BsFillTrashFill title='Delete' /></Link> |
                                                                    <Link to="#" onClick={() => handleAssign(row?.id)}><BsBookmarksFill title='Assign' /></Link>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="9" className="text-center">No tickets available</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <ListProductTicket
                                                customerId={customerId}
                                                handleSubmit={handleSubmit}
                                                control={control}
                                                errors={errors}
                                                onSubmitAssign={onSubmitAssign}
                                                onSubmit={onSubmit}
                                                usersList={usersList}
                                                tickets={productTickets}
                                                reset={reset}
                                                handleColumnChange={handleColumnChange}
                                                handleClose={handleClose}
                                                showEditModal={showEditModal}
                                                setShowEditTicketModal={setShowEditTicketModal}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ToastContainer containerId={'ticketToast'} />

            {/* Modals */}
            {showEditTicketModal && (
                <EditTicketModal
                    show={showEditTicketModal}
                    handleSubmit={handleSubmit}
                    handleClose={handleClose}
                    onSubmit={onSubmit}
                    id={ticketId}
                    control={control}
                    errors={errors}
                    options={columnType === 'priority' ? priorityOptions : statusOptions}
                    type={columnType}
                />
            )}

            {showConfirmModal && (
                <ConfirmModal
                    show={showConfirmModal}
                    onCancel={handleClose}
                    onConfirm={deleteTicket}
                    id={ticketId}
                    heading="Delete Ticket"
                    content="Are you sure you want to delete this ticket?"
                />
            )}

            {showCreateTicketModal && (
                <CreateSubscriptionTicketModal
                    show={showCreateTicketModal}
                    handleClose={handleClose}
                    onTicketSubmit={onTicketSubmit}
                    handleSubmit={handleSubmit}
                    control={control}
                    errors={errors}
                    planData={plan}
                />
            )}

            {showEditModal && (
                <EditSubscriptionTicketDetailsModal
                    show={showEditModal}
                    handleClose={handleClose}
                    handleUpdate={handleUpdate}
                    handleSubmit={handleSubmit}
                    planData={plan}
                    usersList={fetchUsers}
                    ticket={ticket}
                />
            )}

            {showAssignModal && (
                <Assign
                    show={showAssignModal}
                    handleClose={handleClose}
                    onSubmit={onSubmitAssign}
                    handleSubmit={handleSubmit}
                    control={control}
                    errors={errors}
                    planData={plan}
                    AssigneeList={usersList}
                />
            )}
            {showCreateProductTicket && (
                <CreateProductTicket
                    show={showCreateProductTicket}
                    customerId={customerId}
                    priorityOptions={priorityOptions}
                    statusOptions={statusOptions}
                    handleClose={handleClose}
                    handleSubmit={handleSubmit}
                    register={register}
                    control={control}
                    onProductTicketSubmit={onTicketSubmit}
                    productOptions={productOptions}
                />
            )}
        </>
    );
};
